import React, { useState, useEffect, useMemo } from 'react';
import './DeviceGroupsCard.scss';
import { config } from '../../config';

// Type definitions
interface Header {
  key: string;
  sortable: boolean;
}

interface GroupData {
  Name: string;
  'Web Filter': string;
  'Rules Associated': string;
  'Devices Associated': number;
}

interface ApiResponse {
  headers: Header[];
  data: GroupData[];
  records: number;
  size: number;
  start: number;
  end: number;
  page: number;
  pages: number;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface CategoryFilter {
  label: string;
  value: string;
}

// Props interface
interface DeviceGroupsCardProps {
  deviceId: number;
  countPerPage?: number;
  hasSearch?: boolean;
  hasCategoryFilter?: boolean;
  title?: string;
}

const DeviceGroupsCard: React.FC<DeviceGroupsCardProps> = ({ 
  deviceId, 
  countPerPage = 3,
  hasSearch = true,
  hasCategoryFilter = true,
  title = "Device Groups"
}) => {
  // State management
  const [groupsData, setGroupsData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const API_BASE_URL = config.API_URL;

  // Category filters for Web Filter
  const categoryFilters: CategoryFilter[] = [
    { label: 'All Filters', value: 'all' },
    { label: 'Early Teen', value: 'EARLYTEEN' },
    { label: 'No Filter', value: 'No Filter' },
    { label: 'Under 12', value: 'under12' },
    { label: 'Young Adults', value: 'youngAdults' },
  ];

  // Fetch data from API
  useEffect(() => {
    const fetchGroups = async () => {
      if (!deviceId || deviceId <= 0) {
        setError('Valid Device ID is required');
        setLoading(false);
        return;
      }

      if (!API_BASE_URL) {
        setError('API configuration error: REACT_APP_API environment variable is not set');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('loginToken');
        const response = await fetch(
          `${API_BASE_URL}/v2/op/device/${deviceId}/device-groups`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'auth-token': `${token}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        setGroupsData(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching device groups';
        setError(errorMessage);
        console.error('Error fetching device groups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [deviceId, API_BASE_URL]);

  // Handle sorting
  const handleSort = (key: string) => {
    if (!groupsData?.headers.find(header => header.key === key && header.sortable)) {
      return;
    }

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!groupsData?.data) return [];

    let filteredData = groupsData.data.filter(group =>
      Object.values(group).some(value =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    // Apply category filter
    if (selectedCategory !== 'all') {
      filteredData = filteredData.filter(group => 
        group['Web Filter'] === selectedCategory
      );
    }

    // Apply sorting
    if (sortConfig) {
      filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof GroupData];
        const bValue = b[sortConfig.key as keyof GroupData];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [groupsData, searchTerm, sortConfig, selectedCategory]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * countPerPage;
    return processedData.slice(startIndex, startIndex + countPerPage);
  }, [processedData, currentPage, countPerPage]);

  const totalPages = Math.ceil(processedData.length / countPerPage);

  // Event handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRetry = () => {
    setCurrentPage(1);
    setSearchTerm('');
    setSortConfig(null);
    setSelectedCategory('all');
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <i className="fas fa-sort"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="fas fa-sort-up"></i>
      : <i className="fas fa-sort-down"></i>;
  };

  // Get filter badge color
  const getFilterBadgeColor = (filter: string) => {
    switch (filter) {
      case 'EARLYTEEN':
        return 'badge-earlyteen';
      case 'under12':
        return 'badge-under12';
      case 'youngAdults':
        return 'badge-youngadults';
      case 'No Filter':
        return 'badge-nofilter';
      default:
        return 'badge-default';
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="device-groups-card">
        <div className="card-header">
          <div className="header-content">
            <h2 className="card-title">
              <i className="fas fa-users"></i>
              {title}
            </h2>
          </div>
        </div>
        <div className="card-body">
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading device groups...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="device-groups-card">
        <div className="card-header">
          <div className="header-content">
            <h2 className="card-title">
              <i className="fas fa-users"></i>
              {title}
            </h2>
          </div>
        </div>
        <div className="card-body">
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button 
              className="retry-btn"
              onClick={handleRetry}
            >
              <i className="fas fa-redo"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="device-groups-card">
      <div className="card-header">
        <div className="header-content">
          <h2 className="card-title">
            <i className="fas fa-users"></i>
            {title}
          </h2>
        </div>
        <div className="header-actions">
          {hasSearch && (
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          )}
          
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <i className="fas fa-th"></i>
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <i className="fas fa-table"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {groupsData && groupsData.data.length > 0 ? (
          <>
            {viewMode === 'table' ? (
              // Table View
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      {groupsData.headers.map((header) => (
                        <th
                          key={header.key}
                          onClick={() => header.sortable && handleSort(header.key)}
                          className={header.sortable ? 'sortable' : ''}
                        >
                          {header.key}
                          {header.sortable && getSortIcon(header.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((group, index) => (
                      <tr key={index}>
                        <td>
                          <div className="group-name">
                            <i className="fas fa-user-friends"></i>
                            {group.Name}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getFilterBadgeColor(group['Web Filter'])}`}>
                            {group['Web Filter']}
                          </span>
                        </td>
                        <td>
                          <div className="rules-count">
                            <i className="fas fa-sliders-h"></i>
                            {group['Rules Associated']}
                          </div>
                        </td>
                        <td>
                          <div className="devices-count">
                            <i className="fas fa-laptop"></i>
                            {group['Devices Associated']}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Grid View
              <div className="groups-grid">
                {paginatedData.map((group, index) => (
                  <div key={index} className="group-card">
                    <div className="group-card-header">
                      <div className="group-name">
                        <i className="fas fa-user-friends"></i>
                        <h3>{group.Name}</h3>
                      </div>
                      <span className={`badge ${getFilterBadgeColor(group['Web Filter'])}`}>
                        {group['Web Filter']}
                      </span>
                    </div>
                    
                    <div className="group-card-body">
                      <div className="group-stats">
                        <div className="stat-item">
                          <div className="stat-icon">
                            <i className="fas fa-sliders-h"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{group['Rules Associated']}</span>
                            <span className="stat-label">Rules</span>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon">
                            <i className="fas fa-laptop"></i>
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{group['Devices Associated']}</span>
                            <span className="stat-label">Devices</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="group-card-footer">

                      <button className="action-btn manage-btn">
                        <i className="fas fa-cog"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <i className="fas fa-users"></i>
            <p>No device groups found</p>
            {(searchTerm || selectedCategory !== 'all') && (
              <p className="search-hint">Try adjusting your search or filter criteria</p>
            )}
          </div>
        )}
      </div>

      {groupsData && groupsData.data.length > 0 && (
        <div className="card-footer p-2">
          <div className="pagination-info">
            Showing {((currentPage - 1) * countPerPage) + 1} to{' '}
            {Math.min(currentPage * countPerPage, processedData.length)} of{' '}
            {processedData.length} entries
            {(searchTerm || selectedCategory !== 'all') && (
              <span> (filtered from {groupsData.records} total records)</span>
            )}
          </div>

          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={currentPage === page ? 'active' : ''}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceGroupsCard;